import { useIsMounted } from '@databases/react';
import { omit } from 'lodash-es';
import React, {
  createContext,
  type FunctionComponent,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type ValidationData } from './types';

interface FormContextValue {
  _addValidationData: (id: string, data: ValidationData) => void;
  _removeValidationData: (id: string) => void;

  /**
   * Validate all registered fields in the form and then call the callback if there are no validation
   * errors. While the validation of callback is running, a concurrent submission of the form is prevented.
   */
  submit: (callback: () => void | Promise<void>) => Promise<void>;

  /** true if a submission using submit() is currently running */
  isSubmitting: boolean;
}

const FormContext = createContext<FormContextValue | undefined>(undefined);

interface FormProps {
  children: ReactNode;
}

/**
 * Provides a form context that descendant nodes can inject using useForm() and fields can
 * inject their status into using useField(). This makes it possible to get a central overview
 * over the validation status of all fields in the form, and prevent the submission of the form
 * if there are validation errors.
 */
const FormProvider: FunctionComponent<FormProps> = ({ children }) => {
  const [validationData, setValidationData] = useState<Record<string, ValidationData>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const isMounted = useIsMounted();

  const value = useMemo((): FormContextValue => {
    return {
      _addValidationData: (id, data) => {
        if (isMounted.current && validationData[id] !== data) {
          setValidationData({ ...validationData, [id]: data });
        }
      },

      _removeValidationData: (id) => {
        if (isMounted.current) {
          setValidationData(omit(validationData, [id]));
        }
      },

      submit: async (callback) => {
        if (isSubmittingRef.current) {
          return;
        }

        try {
          isSubmittingRef.current = true;
          setIsSubmitting(true);

          Object.values(validationData).forEach((f) => f.touch());
          const errors = await Promise.all(
            Object.values(validationData).map(async (f) => ({
              id: f.id,
              error: await f.errorPromise,
            }))
          );
          const error = errors.find((e) => e.error != null);
          if (error) {
            validationData[error.id]?.focus();
            return;
          }

          await callback();
        } finally {
          isSubmittingRef.current = false;
          if (isMounted.current) {
            setIsSubmitting(false);
          }
        }
      },

      isSubmitting,
    };
  }, [isSubmitting, isMounted, validationData]);

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export default FormProvider;

export function useFormOptional(): FormContextValue | undefined {
  return useContext(FormContext);
}

export function useFormRequired(): FormContextValue {
  const form = useFormOptional();
  if (!form) {
    throw new Error('No Form context found.');
  }
  return form;
}
